import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { MapPin, Trash, Pencil } from "lucide-react"; // Importing an
import {
  AddWorScheduleApi,
  FetchLocationApi,
  FetchWorkSchedulesData,
  deleteWorkScheduleAPI,
  UpdateWorkScheduleApi, // Import the update API
  fetchSubscribedFeaturesAPI,
} from "../../actioncreators/actioncreators";
import "./dashboard.css";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import { toast } from "react-toastify";
import { FaCopy } from "react-icons/fa";
import { IconButton } from "@mui/material";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { orange } from "@mui/material/colors";
import { useSelector } from "react-redux";
import { useSuspension } from "../../contexts/SuspensionContext";

const AddWorkSchedule = () => {
  // Suspension check
  const { isSuspended } = useSuspension();
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(false); // Track if component is mounted
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [previousScheduleData, setPreviousScheduleData] = useState(null);
  const [overtimeStart, setOvertimeStart] = useState("");
  const [showOvertime, setShowOvertime] = useState(false);
  const [isOvertimeEnabled, setIsOvertimeEnabled] = useState(false);
  const [isRestOvertimeEnabled, setIsRestOvertimeEnabled] = useState(false);
  const [overtimeStartBefore, setOvertimeStartBefore] = useState("");
  const [overtimeStartAfter, setOvertimeStartAfter] = useState("");
  const [autoCheckOutAt, setAutoCheckOutAt] = useState("");
  const [allowAutoCheckOut, setAllowAutoCheckOut] = useState(false);
  const [checkInOutWithinPremises, setCheckInOutWithinPremises] =
    useState(false);
  const [autoCheckOutOption, setAutoCheckOutOption] = useState("after_schedule"); // 'after_schedule' or 'specific_time'
  const [enableGeofencing, setEnableGeofencing] = useState(false);
  const [storeActivityImages, setStoreActivityImages] = useState(false);
  const [enableLocationTracking, setEnableLocationTracking] = useState(false);
  const [requireLocationForCheckInOut, setRequireLocationForCheckInOut] =
    useState(false);
  const [enableDigitalWellbeing, setEnableDigitalWellbeing] = useState(false);
  const [enableStoreActivityImages, setEnableStoreActivityImages] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Fixed");
  const [selectedLocationId, setSelectedLocationId] = useState([]);
  const [selectedLocationName, setSelectedLocationName] = useState([]); // State for selected location name
  const [selectedRadius, setSelectedRadius] = useState([]); // State for selected location name
  const [hoveredDay, setHoveredDay] = useState(null); // State to track hovered day
  const [dayTimes, setDayTimes] = useState({
    Monday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
    Tuesday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
    Wednesday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
    Thursday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
    Friday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
    Saturday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
    Sunday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
  });
  const [selectedDays, setSelectedDays] = useState([
    [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
  ]);
  const [workSchedules, setWorkSchedules] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);
  const [activeTab, setActiveTab] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [wsPage, setWsPage] = useState(1);
  const wsPageSize = 10;
  const [breaks, setBreaks] = useState([]);
  const [scheduleName, setScheduleName] = useState("");
  const [selectedWorkArrangement, setSelectedWorkArrangement] =
    useState("Fixed");
  const [selectedOvertimeType, setSelectedOvertimeType] = useState("Hours");

  const [hasGeoFencing, setHasGeoFencing] = useState(false);
  const [hasLocationTracking, setHasLocationTracking] = useState(false);
  const [hasAutoCheckout, setHasAutoCheckout] = useState(false);
  const [hasBreakMonitoring, setHasBreakMonitoring] = useState(false);
  const [hasOvertimeLogging, setHasOvertimeLogging] = useState(false);
  const [hasAppUsageMonitoring, setHasAppUsageMonitoring] = useState(false);
  const [hasActivitySnapshotAuditing, setHasActivitySnapshotAuditing] = useState(false);

  // Get global location filter from Redux
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);

  // Super Admin org filtering support
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';

  // Initial load
  useEffect(() => {
    FetchWorkSchedules();
    FetchLocation();
    FetchSubscribedFeature();
  }, []);

  // Re-fetch when location filter or org selection changes
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    FetchWorkSchedules();
    FetchLocation();
  }, [globalLocationFilter, superAdminSelectedOrgId]);

  const isShiftCheckboxesValid = () => {
    for (const shortDay of selectedDays) {
      const fullDay = shortToFullDay[shortDay];
      const daySlots = dayTimes[fullDay] || [];

      // Check if at least one checkbox is checked for the day
      const hasCheckedBox = daySlots.some(
        (slot) => slot.isShiftStart || slot.isShiftEnd
      );

      if (!hasCheckedBox) {
        return false;
      }
    }
    return true;
  };

  const isFormValid = () => {
    // Check if schedule name is not empty
    const isScheduleNameValid = scheduleName.trim() !== "";

    // Check if at least one location is selected ONLY if geofencing is enabled
    const isLocationValid =
      !enableGeofencing || (enableGeofencing && selectedLocationId?.length > 0);

    // Check if at least one day is selected
    const isDaysValid = selectedDays?.length > 0;

    // Check if selected days have valid time entries
    const areTimesValid = selectedDays.every((shortDay) => {
      const fullDay = shortToFullDay[shortDay];
      const times = dayTimes[fullDay] || [];
      return (
        Array.isArray(times) &&
        times.every((timeSlot) => {
          const isStartValid =
            !timeSlot.start || (timeSlot.start && timeSlot.start.trim() !== "");
          const isEndValid =
            !timeSlot.end || (timeSlot.end && timeSlot.end.trim() !== "");
          return isStartValid && isEndValid;
        })
      );
    });

    // Log validation results for debugging
    console.log("Validation Results:", {
      scheduleName: isScheduleNameValid,
      location: isLocationValid,
      days: isDaysValid,
      times: areTimesValid,
    });

    return (
      isScheduleNameValid && isLocationValid && isDaysValid && areTimesValid
    );
  };

  const FetchLocation = async () => {
    setIsLoading(true);
    setError(null);
    const payload = {
      entity_type: "location",
      pagenumber: 1,
      pageSize: 1000,
      parameter: [
        {
          columnName: "status",
          condition: "=",
          value: "ACTIVE",
        },
      ],
    };

    // Add org_id for Super Admin viewing specific organization
    if (isSuperAdmin && superAdminSelectedOrgId) {
      payload.org_id = superAdminSelectedOrgId;
    }

    try {
      const response = await FetchLocationApi(payload); // Fetch from your API

      // Log the entire response to debug
      console.log("Location:", response);

      // Accessing the data inside the response
      const { businessStatusCode, summaryData } = response.data;

      // Check if the response contains the data as expected
      const { viewListData } = response.data; // Update this line as per your actual response structure

      // Store the location names in the state
      setLocations(viewListData || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const FetchSubscribedFeature = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchSubscribedFeaturesAPI(); // Execute the function call
      console.log("Subscribed Features:", response);

      if (response && response.data) {
        // Accessing the data inside the response
        const { businessStatusCode, features } = response.data;

        // If features is null/empty, enable everything by default
        if (!features || !Array.isArray(features) || features.length === 0) {
          setHasGeoFencing(true);
          setHasLocationTracking(true);
          setHasAutoCheckout(true);
          setHasBreakMonitoring(true);
          setHasOvertimeLogging(true);
          setHasAppUsageMonitoring(true);
          setHasActivitySnapshotAuditing(true);
        } else {
          // Reset all feature flags to false, then enable based on subscription
          setHasGeoFencing(false);
          setHasLocationTracking(false);
          setHasAutoCheckout(false);
          setHasBreakMonitoring(false);
          setHasOvertimeLogging(false);
          setHasAppUsageMonitoring(false);
          setHasActivitySnapshotAuditing(false);

          features.forEach((feature) => {
            if (feature.status === "ACTIVE") {
              switch (feature.feature_type) {
                case "Geo-Fencing":
                  setHasGeoFencing(true);
                  break;
                case "Location Tracking":
                  setHasLocationTracking(true);
                  break;
                case "Automatic Check-out":
                  setHasAutoCheckout(true);
                  break;
                case "Break Monitoring":
                  setHasBreakMonitoring(true);
                  break;
                case "Overtime Logging":
                  setHasOvertimeLogging(true);
                  break;
                case "App Usage Monitoring":
                  setHasAppUsageMonitoring(true);
                  break;
                case "Activity Snapshot Audit":
                  setHasActivitySnapshotAuditing(true);
                  break;
                default:
                  break;
              }
            }
          });
        }

        console.log("Subscribed features processed successfully");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching subscribed features:", err);
      // On error, enable all features so the form is usable
      setHasGeoFencing(true);
      setHasLocationTracking(true);
      setHasAutoCheckout(true);
      setHasBreakMonitoring(true);
      setHasOvertimeLogging(true);
      setHasAppUsageMonitoring(true);
      setHasActivitySnapshotAuditing(true);
    } finally {
      setIsLoading(false);
    }
  };

  const FetchWorkSchedules = async () => {
    try {
      setIsLoading(true);
      const effectiveOrgId = isSuperAdmin && superAdminSelectedOrgId ? superAdminSelectedOrgId : null;
      const data = await FetchWorkSchedulesData(globalLocationFilter, effectiveOrgId); // Pass location filter and org_id
      setActiveTab("addSchedule");
      if (data && data.viewListData && data.viewListData?.length > 0) {
        setWorkSchedules(data.viewListData); // Populate the schedules
        setActiveTab(data.viewListData[0].work_schedule_id); // Optionally, set the first schedule as active
      } else {
        setWorkSchedules([]);
        setActiveTab("addSchedule"); // Set to 'Add New Schedule' if no schedules are found
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching work schedules", error);
      setError(error);
      setIsLoading(false);
    }
  };

  const handleLocationChange = (selectedOptions) => {
    // React-Select returns selected options as an array of objects
    setSelectedLocationId(selectedOptions.map((option) => option.value));
    setSelectedLocationName(selectedOptions.map((option) => option.label));
    setSelectedRadius(selectedOptions.map((option) => option.radius));
  };

  const handleWorkArrangementClick = (option) => {
    setSelectedWorkArrangement(option);
    if (option === "Flexible") {
      setSelectedDays([]); // Do not select all days by default
    }
  };

  const handleOvertimeTypeClick = (option) => {
    setSelectedOvertimeType(option);
  };

  const optionStyle = (option) =>
    `col text-center py-2 ${
      selectedOption === option
        ? "bg-red-100 text-red-600 border border-red-400"
        : "border-end"
    } cursor-pointer`;

  const handleDayClick = (day) => {
    setSelectedDays((prevDays) => {
      if (prevDays?.includes(day)) {
        return prevDays.filter((d) => d !== day); // Remove day if already selected
      } else {
        // Maintain order when adding back
        const newDays = [...prevDays, day];
        const fullDay = shortToFullDay[day];
        setDayTimes((prevTimes) => ({
          ...prevTimes,
          [fullDay]: [
            { start: "", end: "", isShiftStart: true, isShiftEnd: true },
          ],
        }));
        return newDays.sort(
          (a, b) =>
            [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ].indexOf(a) -
            [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ].indexOf(b)
        );
      }
    });
  };
  const handleTimeChange = (shortDay, timeType, value, index) => {
    const fullDay = shortToFullDay[shortDay];
    if (selectedDays?.includes(shortDay)) {
      setDayTimes((prevTimes) => {
        const dayTimes = prevTimes[fullDay] || [];
        const updatedTimes = dayTimes.map((timeSlot, i) =>
          i === index ? { ...timeSlot, [timeType]: value } : timeSlot
        );
        return {
          ...prevTimes,
          [fullDay]: updatedTimes,
        };
      });
    }
  };

  const handleTimeChangeFlexible = (fullDay, value) => {
    setDayTimes((prevTimes) => ({
      ...prevTimes,
      [fullDay]: {
        time: value,
      },
    }));
  };

  const dayStyle = (day) =>
    `col text-center py-2 ${
      selectedDays?.includes(day)
        ? "bg-red-100 text-red-600 border border-red-400"
        : "border-end"
    } cursor-pointer`;

  const handleAddTimeSlot = (fullDay, index) => {
    setDayTimes((prevTimes) => {
      const dayTimes = prevTimes[fullDay] || [];
      const newTimeSlot = {
        start: "",
        end: "",
        isShiftStart: true,
        isShiftEnd: true,
      };
      const updatedTimes = [
        ...dayTimes.slice(0, index + 1),
        newTimeSlot,
        ...dayTimes.slice(index + 1),
      ];
      return {
        ...prevTimes,
        [fullDay]: updatedTimes,
      };
    });
  };

  const handleRemoveTimeSlot = (fullDay, index) => {
    setDayTimes((prevTimes) => {
      const dayTimes = prevTimes[fullDay] || [];
      const updatedTimes = dayTimes.filter((_, i) => i !== index);
      return {
        ...prevTimes,
        [fullDay]: updatedTimes,
      };
    });
  };

  const formatSchedulePayload = () => {
    const schedulePayload = {};

    Object.keys(shortToFullDay).forEach((shortDay) => {
      const fullDay = shortToFullDay[shortDay];
      if (selectedDays.includes(shortDay)) {
        const dayTimesArray = dayTimes[fullDay] || []; // Get the times for the selected day
        const slots = dayTimesArray.map((timeSlot, index) => ({
          shift_start: timeSlot.isShiftStart || false,
          start_time: timeSlot?.start || "",
          end_time: timeSlot?.end || "",
          shift_end: timeSlot.isShiftEnd || false,
        }));

        schedulePayload[fullDay] = {
          slots,
          rest_day: false,
        };
      } else {
        schedulePayload[fullDay] = {
          rest_day: true,
        };
      }
    });

    console.log("Formatted Schedule Payload:", schedulePayload);
    return schedulePayload;
  };

  useEffect(() => {
    const schedulePayload = formatSchedulePayload();
    console.log("Schedule Payload:", schedulePayload);
  }, [dayTimes, selectedDays, selectedOption]);

  const handleUpdate = async () => {
    if (!isFormValid()) {
      return;
    }

    // Create base schedule data
    const newScheduleData = {
      work_schedule_id: scheduleData.work_schedule_id,
      org_id: scheduleData.org_id,
      schedule_name: scheduleName,
      work_arrangement: selectedOption,
      status: "ACTIVE",
      schedule: formatSchedulePayload(),
      breaks: {
        breaks,
      },
      description: "updating schedule",
      policies: {
        location_tracking: enableLocationTracking,
        checkout_policies: {
          ...(allowAutoCheckOut && autoCheckOutOption === "after_schedule"
            ? { allow_auto_check_out: true }
            : autoCheckOutOption === "specific_time" && autoCheckOutAt
            ? { allow_auto_check_out: false, allow_auto_check_out_at: autoCheckOutAt }
            : { allow_auto_check_out: false }),
          auto_check_out_on_exit: checkInOutWithinPremises,
        },
        geo_fencing: {
          enable_geofencing: enableGeofencing,
          require_location_for_check_in_out: requireLocationForCheckInOut,
        },
        overtime_policies: {
          daily_overtime: {
            enabled: isOvertimeEnabled,
            measurement_unit: selectedOvertimeType,
            start_overtime_after: overtimeStartAfter,
            start_overtime_before: overtimeStartBefore,
          },
          rest_day_overtime: {
            enabled: isRestOvertimeEnabled,
          },
        },
        app_usage_tracking: enableDigitalWellbeing,
        store_activity_images: storeActivityImages,
      },
    };

    // Only add location if locations are selected
    if (selectedLocationId && selectedLocationId.length > 0) {
      newScheduleData.location = {
        locationDetails: selectedLocationId.map((locationId, index) => {
          const locationName = selectedLocationName[index];
          const radius = selectedRadius[index];
          return {
            locationId: locationId,
            locationName: locationName,
            radius: radius,
          };
        }),
      };
    }

    const payload = {
      old_data: scheduleData,
      new_data: newScheduleData,
    };

    try {
      const response = await UpdateWorkScheduleApi(payload);
      console.log("Schedule updated successfully:", response);
      if (response.businessStatusCode === 2) {
        toast.success(response.message);
        setIsEditing(false); // Exit editing mode
        FetchWorkSchedules();
      }
    } catch (error) {
      console.error("Error updating schedule:", error);
    }
  };

  const handleSubmit = async () => {
    if (isEditing) {
      handleUpdate();
    } else {
      if (!isFormValid()) {
        return;
      }

      // Create base schedule data without location
      const newScheduleData = {
        entity_type: "work_schedule",
        comment: "comment added",
        columns: {
          schedule_name: scheduleName,
          work_arrangement: selectedOption,
          ...breakPayload,
          schedule: formatSchedulePayload(),
          description: "adding new schedule",
          policies: {
            location_tracking: enableLocationTracking,
            checkout_policies: {
              ...(allowAutoCheckOut && autoCheckOutOption === "after_schedule"
                ? { allow_auto_check_out: true }
                : autoCheckOutOption === "specific_time" && autoCheckOutAt
                ? { allow_auto_check_out: false, allow_auto_check_out_at: autoCheckOutAt }
                : { allow_auto_check_out: false }),
              auto_check_out_on_exit: checkInOutWithinPremises,
            },
            geo_fencing: {
              enable_geofencing: enableGeofencing,
              require_location_for_check_in_out: requireLocationForCheckInOut,
            },
            overtime_policies: {
              daily_overtime: {
                enabled: isOvertimeEnabled,
                measurement_unit: selectedOvertimeType,
                start_overtime_after: overtimeStartAfter,
                start_overtime_before: overtimeStartBefore,
              },
              rest_day_overtime: {
                enabled: isRestOvertimeEnabled,
              },
            },
            app_usage_tracking: enableDigitalWellbeing,
            store_activity_images: storeActivityImages,
          },
        },
      };

      // Only add location if locations are selected
      if (selectedLocationId && selectedLocationId.length > 0) {
        newScheduleData.columns.location = {
          locationDetails: selectedLocationId.map((locationId, index) => {
            const locationName = selectedLocationName[index];
            const radius = selectedRadius[index];
            return {
              locationId: locationId,
              locationName: locationName,
              radius: radius,
            };
          }),
        };
      }

      try {
        const response = await AddWorScheduleApi(newScheduleData);
        console.log("Schedule submitted successfully:", response);
        if (response.status === 200) {
          toast.success(response.data.message);
          FetchWorkSchedules();
        }
      } catch (error) {
        console.error("Error submitting schedule:", error);
      }
    }
  };

  const handleDelete = async (workScheduleId) => {
    try {
      const payload = {
        entity_type: "work_schedule",
        parameter: [
          {
            columnName: "work_schedule_id",
            condition: "=",
            value: workScheduleId,
          },
        ],
      };

      // Make the API call here
      const response = await deleteWorkScheduleAPI(payload);

      if (response?.businessStatusCode === 2) {
        toast.success("Work Schedule deleted successfully");
        FetchWorkSchedules();
      }

      // Handle successful response
      console.log("User deleted successfully");
    } catch (error) {
      // Handle error
      console.error("Error deleting user:", error);
    }
  };

  // Helper function to normalize break data
  const normalizeBreakData = (breaks) => {
    if (!breaks || !Array.isArray(breaks)) {
      return [];
    }
    return breaks.map(breakItem => {
      // Extract hours and minutes from duration string if they don't exist
      const durationMatch = breakItem.duration?.match(/(\d+)h\s*(\d+)m/);
      const hours = durationMatch ? durationMatch[1] : "0";
      const minutes = durationMatch ? durationMatch[2] : "0";

      return {
        ...breakItem,
        // Map 'name' from database to 'breakName' for UI
        breakName: breakItem.breakName || breakItem.name || "",
        hours: breakItem.hours || hours,
        minutes: breakItem.minutes || minutes
      };
    });
  };

  // Update the handleDurationChange function
  const handleDurationChange = (index, field, value) => {
    const updatedBreaks = [...breaks];

    // Ensure value is between 0-23 for hours and 0-59 for minutes
    let sanitizedValue = parseInt(value) || 0;
    if (field === "hours") {
      sanitizedValue = Math.min(Math.max(sanitizedValue, 0), 23);
    } else {
      sanitizedValue = Math.min(Math.max(sanitizedValue, 0), 59);
    }

    updatedBreaks[index][field] = sanitizedValue.toString();

    // Update the duration field in "00h 15m" format
    const hours = (updatedBreaks[index].hours || "0").padStart(2, "0");
    const minutes = (updatedBreaks[index].minutes || "0").padStart(2, "0");
    updatedBreaks[index].duration = `${hours}h ${minutes}m`;

    setBreaks(updatedBreaks);
  };

  // Update the handleAddBreak function
  const handleAddBreak = () => {
    const newBreak = {
      breakName: "",
      duration: "00h 00m", // Update initial format
      hours: "0", // Initialize hours field
      minutes: "0", // Initialize minutes field
      paid: false,
      allowed_time_range: {
        enabled: false,
        start_time: "10:00",
        end_time: "11:00",
      },
    };
    setBreaks([...breaks, newBreak]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedBreaks = [...breaks];
    if (field === "enabled" || field === "start_time" || field === "end_time") {
      updatedBreaks[index].allowed_time_range[field] = value;
    } else {
      updatedBreaks[index][field] = value;
    }
    setBreaks(updatedBreaks);
  };

  const breakPayload = {
    breaks: {
      breaks,
    },
  };

  const handleRemoveBreak = (index) => {
    const updatedBreaks = breaks.filter((_, i) => i !== index);
    setBreaks(updatedBreaks);
  };

  useEffect(() => {
    if (workSchedules && workSchedules?.length > 0) {
      setActiveTab(workSchedules[0].work_schedule_id);
      setScheduleData(workSchedules[0]);
    }
  }, [workSchedules]);
  const handleTabClick = (scheduleId) => {
    setActiveTab(scheduleId);
    if (scheduleId === "addSchedule") {
      setScheduleData(null);
      clearFormValues(); // This will now clear all checkbox values
      setIsEditing(false); // Also ensure editing mode is disabled
      // Call the API to fetch subscribed features when "Add New Schedule" button is clicked
      FetchSubscribedFeature();
    } else {
      const selectedSchedule = workSchedules.find(
        (schedule) => schedule.work_schedule_id === scheduleId
      );
      setScheduleData(selectedSchedule);
    }
  };

  const clearFormValues = () => {
    setScheduleName("");
    setSelectedOption("Fixed");
    setSelectedLocationId([]);
    setSelectedLocationName([]);
    setSelectedRadius([]);
    setBreaks([]);
    setIsOvertimeEnabled(false);
    setOvertimeStartAfter("");
    setOvertimeStartBefore("");
    setIsRestOvertimeEnabled(false);
    setAutoCheckOutAt("");
    setAllowAutoCheckOut(false); // Set to false instead of true
    setAutoCheckOutOption("after_schedule");
    setCheckInOutWithinPremises(false); // Set to false instead of true
    setEnableGeofencing(false);
    setEnableLocationTracking(false); // Set to false instead of true
    setRequireLocationForCheckInOut(false); // Set to false instead of true
    setEnableDigitalWellbeing(false); // Set to false instead of true
    setEnableStoreActivityImages(false); // Set to false instead of true
    setSelectedDays([]);
    setDayTimes({
      Monday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
      Tuesday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
      Wednesday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
      Thursday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
      Friday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
      Saturday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
      Sunday: [{ start: "", end: "", isShiftStart: true, isShiftEnd: true }],
    });
  };

  const filteredSchedules = workSchedules.filter((schedule) =>
    schedule.schedule_name.toLowerCase()?.includes(searchQuery.toLowerCase())
  );

  // Reset to page 1 when search changes
  React.useEffect(() => { setWsPage(1); }, [searchQuery]);

  const shortToFullDay = {
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
    Sun: "Sunday",
  };
  const copyTimesFromFirst = (firstDay) => {
    const firstPopulatedDay = Object.keys(dayTimes).find(
      (day) =>
        !["Sat", "Sun"].includes(day) &&
        (selectedOption === "Flexible"
          ? dayTimes[day].time
          : dayTimes[day].start && dayTimes[day].end)
    );

    if (firstPopulatedDay) {
      const { start, end, time } = dayTimes[firstPopulatedDay];
      setDayTimes((prev) => {
        const newTimes = { ...prev };
        [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ].forEach((day) => {
          if (selectedOption === "Flexible") {
            newTimes[day] = { time };
          } else {
            newTimes[day] = { start, end };
          }
        });
        return newTimes;
      });
    }
  };
  const handleEditClick = (schedule) => {
    setPreviousScheduleData(scheduleData); // Save the current schedule data before editing
    // setIsEditing(true);
  };
  const handleCancelClick = () => {
    setIsEditing(false);
    setScheduleData(previousScheduleData); // Restore the previous schedule data
  };

  const hideScrollbarStyle = {
    maxHeight: "100vh", // Change from 69vh to 100vh
    overflowY: "auto",
    scorllbarWidth: "none",
    msOverflowStyle: "none",
  };

  const hideScrollbar = {
    "&::-webkit-scrollbar": {
      display: "none", // For Chrome, Safari, and Opera
    },
  };
  const handleOvertimeSwitchChange = (e) => {
    setIsOvertimeEnabled(e.target.checked);
  };

  const handleOvertimeStartBeforeChange = (e) => {
    setOvertimeStartBefore(e.target.value);
  };

  const handleautoCheckOutAtChange = (e) => {
    setAutoCheckOutAt(e.target.value);
  };

  const handleAutoCheckOutOptionChange = (option) => {
    setAutoCheckOutOption(option);
    if (option === "after_schedule") {
      setAutoCheckOutAt("");
    }
  };

  const handleOvertimeStartAfterChange = (e) => {
    setOvertimeStartAfter(e.target.value);
  };

  const handleMouseEnter = (day) => {
    setHoveredDay(day);
  };

  const handleMouseLeave = () => {
    setHoveredDay(null);
  };

  const locationOptions = locations?.map((location) => ({
    value: location.location_id,
    label: location.location_name,
    radius: location.radius,
  }));

  useEffect(() => {
    if (scheduleData) {
      setScheduleName(scheduleData.schedule_name);
      setSelectedOption(scheduleData.work_arrangement);
      setSelectedLocationId(
        scheduleData?.location?.locationDetails.map((loc) => loc.locationId)
      );
      setSelectedLocationName(
        scheduleData?.location?.locationDetails.map((loc) => loc.locationName)
      );
      setSelectedRadius(
        scheduleData?.location?.locationDetails.map((loc) => loc.radius)
      );
      setBreaks(normalizeBreakData(scheduleData?.breaks?.breaks));
      setIsOvertimeEnabled(
        scheduleData.policies?.overtime_policies?.daily_overtime?.enabled
      );
      setOvertimeStartAfter(
        scheduleData.policies?.overtime_policies?.daily_overtime
          ?.start_overtime_after
      );
      setOvertimeStartBefore(
        scheduleData.policies?.overtime_policies?.daily_overtime
          ?.start_overtime_before
      );
      setIsRestOvertimeEnabled(
        scheduleData.policies?.overtime_policies?.rest_day_overtime?.enabled
      );
      setAutoCheckOutAt(
        scheduleData.policies?.checkout_policies?.auto_check_out_at
      );
      setAllowAutoCheckOut(
        scheduleData.policies?.checkout_policies?.allow_auto_check_out ||
        !!scheduleData.policies?.checkout_policies?.allow_auto_check_out_at
      );
      
      // Determine the option based on the response
      if (scheduleData.policies?.checkout_policies?.allow_auto_check_out_at) {
        setAutoCheckOutOption("specific_time");
      } else {
        setAutoCheckOutOption("after_schedule");
      }
      setCheckInOutWithinPremises(
        scheduleData.policies?.checkout_policies?.auto_check_out_on_exit
      );
      setEnableGeofencing(
        scheduleData.policies?.geo_fencing?.enable_geofencing
      );
      setEnableLocationTracking(
        scheduleData.policies?.location_tracking
      );
      setRequireLocationForCheckInOut(
        scheduleData.policies?.geo_fencing
          ?.require_location_for_check_in_out
      );
      setEnableDigitalWellbeing(
        scheduleData.policies?.app_usage_tracking
      );
      setEnableStoreActivityImages(
        scheduleData.policies?.store_activity_images
      );

      const selectedDays = Object.keys(scheduleData?.schedule).filter(
        (day) => !scheduleData.schedule[day].rest_day
      );
      setSelectedDays(
        selectedDays?.map((fullDay) =>
          Object.keys(shortToFullDay).find(
            (shortDay) => shortToFullDay[shortDay] === fullDay
          )
        )
      );

      const mappedDayTimes = Object.keys(shortToFullDay).reduce(
        (acc, shortDay) => {
          const fullDay = shortToFullDay[shortDay];
          const daySchedule = scheduleData.schedule[fullDay];
          if (daySchedule?.slots) {
            acc[fullDay] = daySchedule.slots.map((slot) => ({
              start: slot.start_time,
              end: slot.end_time,
              isShiftStart: slot.shift_start, // Map shift start
              isShiftEnd: slot.shift_end, // Map shift end
            }));
          }
          return acc;
        },
        {}
      );
      setDayTimes(mappedDayTimes);
    }
  }, [scheduleData]);

  // Add this function near your other handlers
  const handleCopyToAll = (sourceDay, e) => {
    // Prevent form submission when the copy button is clicked
    if (e) e.preventDefault();

    const sourceDayTimes = dayTimes[sourceDay];
    if (!sourceDayTimes) return;

    setDayTimes((prevTimes) => {
      const newTimes = { ...prevTimes };
      Object.keys(shortToFullDay).forEach((day) => {
        if (selectedDays.includes(day)) {
          const fullDay = shortToFullDay[day];
          newTimes[fullDay] = sourceDayTimes.map((slot) => ({
            ...slot,
            start: slot.start,
            end: slot.end,
            isShiftStart: slot.isShiftStart,
            isShiftEnd: slot.isShiftEnd,
          }));
        }
      });
      return newTimes;
    });
  };

  // Show "Select Organization" message if Super Admin hasn't selected an org
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen" style={{ backgroundColor: "#EFF0F4" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Work Schedules</h1>
            <p className="text-sm text-gray-1000">Super Admin View</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-12 mt-8">
          <svg
            className="w-24 h-24 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center max-w-md">
            Please select an organization from the dropdown in the header to view and manage its work schedules.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 m-0" style={{ minHeight: "100vh" }}>
      {" "}
      {/* Add these styles */}
      <div className="row" style={{ backgroundColor: "#EFF0F4" }}>
        {" "}
        {/* Add minHeight: '100vh' */}
        <div className="col-12 col-md-12 col-lg-12 p-3 d-flex justify-content-between border-bottom text-black-50">
          <input
            type="text"
            placeholder="Search Schedule"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border border-dark-300 border-top-0 border-left-0 border-right-0 p-2 w-25 bg-white shadow"
          />

          <ArthaContinueButton
            onClick={isSuspended ? undefined : () => handleTabClick("addSchedule")}
            disabled={isSuspended}
            title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
            className={` ${
              activeTab === "addSchedule" ? "active" : ""
            } ms-auto`}
            style={{
              padding: "15px 15px",
              cursor: isSuspended ? "not-allowed" : "pointer",
              width: "20%",
              opacity: isSuspended ? 0.5 : 1,
            }}
          >
            <b>+</b> &nbsp; Add New Schedule
          </ArthaContinueButton>
        </div>
        <div
          className="col-4 col-md-3 text-center border-end text-black-50 bg-white shadow rounded p-0"
          style={{ height: 'calc(100vh - 151px)', display: 'flex', flexDirection: 'column' }}
        >
          {/* Header */}
          <p className="mt-2 pb-2 border-bottom text-black-50 fw-bold w-full items-center" style={{ flexShrink: 0 }}>
            Workschedule List
          </p>

          {/* Scrollable list */}
          <div style={{ flex: '1 1 auto', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', padding: '0 12px' }}>
            {filteredSchedules?.length > 0 ? (
              filteredSchedules.slice((wsPage - 1) * wsPageSize, wsPage * wsPageSize).map((schedule) => (
                <div
                  key={schedule.work_schedule_id}
                  onClick={() => handleTabClick(schedule.work_schedule_id)}
                  className="items-center px-3 shadow-sm"
                  style={{
                    padding: "6px",
                    backgroundColor: "#ffffff",
                    color: activeTab === schedule.work_schedule_id ? "#FF8800" : "#333",
                    marginBottom: "1rem",
                    border: activeTab === schedule.work_schedule_id ? "2px solid rgb(254, 137, 4)" : "1px solid #eeeeee",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    borderRadius: "20px",
                  }}
                >
                  {schedule.schedule_name}
                </div>
              ))
            ) : (
              <div className="text-center" style={{ padding: "15px", color: "#888" }}>
                No schedules found
              </div>
            )}
          </div>

          {/* Pagination — fixed footer, identical to Tracking */}
          {filteredSchedules?.length > 0 && (() => {
            const totalWsPages = Math.ceil(filteredSchedules.length / wsPageSize);
            const WING = 1;
            let start = Math.max(1, wsPage - WING);
            let end = Math.min(totalWsPages, wsPage + WING);
            if (end - start < 2) {
              if (start === 1) end = Math.min(totalWsPages, start + 2);
              else start = Math.max(1, end - 2);
            }
            const pageWindow = [];
            for (let i = start; i <= end; i++) pageWindow.push(i);
            const isFirst = wsPage === 1;
            const isLast = wsPage === totalWsPages;
            return (
              <div className="lt-footer">
                <span className="lt-page-info">{wsPage} / {totalWsPages}</span>
                <div className="lt-pagination">
                  <button className="lt-page-btn lt-page-nav" onClick={() => setWsPage(1)} disabled={isFirst} title="First page">«</button>
                  <button className="lt-page-btn lt-page-nav" onClick={() => setWsPage(wsPage - 1)} disabled={isFirst} title="Previous page">‹</button>
                  {pageWindow[0] > 1 && (
                    <>
                      <button className="lt-page-btn" onClick={() => setWsPage(1)}>1</button>
                      {pageWindow[0] > 2 && <span className="lt-page-ellipsis">…</span>}
                    </>
                  )}
                  {pageWindow.map(n => (
                    <button key={n} className={`lt-page-btn${n === wsPage ? ' lt-page-btn--active' : ''}`} onClick={() => setWsPage(n)}>{n}</button>
                  ))}
                  {pageWindow[pageWindow.length - 1] < totalWsPages && (
                    <>
                      {pageWindow[pageWindow.length - 1] < totalWsPages - 1 && <span className="lt-page-ellipsis">…</span>}
                      <button className="lt-page-btn" onClick={() => setWsPage(totalWsPages)}>{totalWsPages}</button>
                    </>
                  )}
                  <button className="lt-page-btn lt-page-nav" onClick={() => setWsPage(wsPage + 1)} disabled={isLast} title="Next page">›</button>
                  <button className="lt-page-btn lt-page-nav" onClick={() => setWsPage(totalWsPages)} disabled={isLast} title="Last page">»</button>
                </div>
              </div>
            );
          })()}
        </div>
        {/* Form Section */}
        <div
          className="col-12 col-md-9 text-sm shadow-sm"
          style={{ ...hideScrollbarStyle, height: "100vh" }}
        >
          {" "}
          {/* Add height: '100vh' */}
          {/* Show form for selected schedule or 'Add New Schedule' tab */}
          {activeTab === "addSchedule" || scheduleData === null || isEditing ? (
            <form
              className="card bg-white mx-auto px-4 py-1"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              // Apply scrollbar hiding styles to the form
            >
              <h5 className=" font-semibold text-gray-700 mb-2">
                {isEditing ? "Edit Schedule" : "Schedule"}
              </h5>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-4 py-2 mt-2 ">
                <div className="form-group row">
                  <label htmlFor="scheduleName" className="col-sm-3 text-sm">
                    Schedule Name :
                  </label>
                  <div className="col-sm-8">
                    <input
                      type="text"
                      id="scheduleName"
                      value={scheduleName}
                      placeholder="Enter Schedule Name"
                      onChange={(e) => setScheduleName(e.target.value)}
                      className="form-control text-sm"
                    />
                  </div>
                </div>

                {/* Work Arrangement */}
                <div className="form-group row">
                  <label
                    htmlFor="workArrangement"
                    className="col-sm-3 col-form-label"
                  >
                    Work Arrangement:
                  </label>
                  <div className="col-sm-8">
                    <div className="d-flex border rounded">
                      {["Fixed", "Flexible", "Weekly"]?.map((option) => (
                        <div
                          key={option}
                          // className={optionStyle(option)}
                          className={`${
                            selectedWorkArrangement === option
                              ? "bg-red-100 text-red-600 border border-red-400"
                              : "border-end"
                          } col text-center py-2 cursor-pointer ${
                            option !== "Fixed" && option !== "Flexible"
                              ? "disabled"
                              : ""
                          }`}
                          onClick={() => handleWorkArrangementClick(option)}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Days of the Week */}
                <div className="form-group row mt-3">
                  <label
                    htmlFor="daysOfWeek"
                    className="col-sm-3 col-form-label"
                  >
                    Days of the Week:
                  </label>
                  <div className="col-sm-8">
                    <div className="d-flex border rounded">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]?.map(
                        (day) => (
                          <div
                            key={day}
                            className={dayStyle(day)}
                            onClick={() => handleDayClick(day)}
                          >
                            {/* {day === 'T2' ? 'Th' : day} */}
                            {day}
                          </div>
                        )
                      )}
                    </div>
                    {selectedDays?.length === 0 && (
                      <span
                        className="text-sm fw-semibold"
                        style={{ color: "red" }}
                      >
                        Select at least one day of the week
                      </span>
                    )}
                  </div>
                </div>

                {selectedOption === "Flexible" ? (
                  <div className="mt-3" style={{ marginLeft: "20%" }}>
                    {Object.entries(shortToFullDay)?.map(
                      ([shortDay, fullDay]) => {
                        const isSelected = selectedDays.includes(shortDay);
                        return (
                          <div
                            key={shortDay}
                            className="form-group"
                            onMouseEnter={() => handleMouseEnter(fullDay)}
                            onMouseLeave={handleMouseLeave}
                          >
                            <div className="d-flex align-items-center mb-2">
                              <div className="col-3 text-sm ">{fullDay}</div>
                              {isSelected ? (
                                <>
                                  <input
                                    type="time"
                                    className="form-control col-3 mx-2"
                                    value={dayTimes[fullDay]?.time || ""}
                                    onChange={(e) =>
                                      handleTimeChangeFlexible(
                                        fullDay,
                                        e.target.value
                                      )
                                    }
                                  />
                                  {hoveredDay === fullDay && (
                                    <button
                                      type="button" // Add this to prevent form submission
                                      onClick={(e) => {
                                        e.preventDefault();
                                        copyTimesFromFirst("Monday");
                                      }}
                                      style={{
                                        backgroundColor: "transparent",
                                        border: "none",
                                        color: "#FF8800",
                                        padding: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                      }}
                                      className="text-sm"
                                      title="Copy time to all days"
                                    >
                                      <FaCopy
                                        size={10}
                                        style={{ marginRight: "4px" }}
                                      />
                                      Copy
                                    </button>
                                  )}
                                </>
                              ) : (
                                <div
                                  className="col-3"
                                  style={{
                                    color: "grey",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Rest Day
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <div
                    className=" mt-3 col-12 col-md-11 text-sm border rounded mt-2 py-2 "
                    style={{ marginLeft: "11%" }}
                  >
                    <div className="row pb-2 border-bottom">
                      <div className="col-2 ">Days</div>
                      <div className="col-10">
                        <div className="row ">
                          <div className="col-auto">Shift Start</div>
                          <div className="col-3">From</div>
                          <div className="col-2">To</div>
                          <div className="col-auto ms-3">Shift End</div>
                        </div>
                      </div>
                    </div>
                    {Object.entries(shortToFullDay)?.map(
                      ([shortDay, fullDay]) => {
                        const isSelected = selectedDays?.includes(shortDay);
                        const daySlots = dayTimes[fullDay] || [];
                        const hasCheckedBox = daySlots.some(
                          (slot) => slot.isShiftStart || slot.isShiftEnd
                        );

                        return (
                          <div
                            key={shortDay}
                            className="row  align-items-center py-2 border-bottom text-sm"
                          >
                            <div
                              className="col-2 "
                              style={{ fontSize: "13px" }}
                            >
                              {fullDay}
                            </div>
                            <div className="col-10">
                              {isSelected ? (
                                <>
                                  {(
                                    dayTimes[fullDay] || [
                                      { start: "", end: "" },
                                    ]
                                  ).map((timeSlot, index, array) => (
                                    <div key={index}>
                                      <div className="row text-center align-items-center">
                                        <div className="col-auto">
                                          <Checkbox
                                            checked={
                                              timeSlot.isShiftStart || false
                                            }
                                            onChange={(e) => {
                                              const updatedTimes = {
                                                ...dayTimes,
                                              };
                                              updatedTimes[fullDay][
                                                index
                                              ].isShiftStart = e.target.checked;
                                              setDayTimes(updatedTimes);
                                            }}
                                            sx={{
                                              color: orange[800],
                                              "&.Mui-checked": {
                                                color: orange[600],
                                              },
                                              "& .MuiSvgIcon-root": {
                                                // Add this style block to control size
                                                fontSize: 28, // Increase icon size (default is 24)
                                                width: "1.5em", // Increase width
                                                height: "1.5em", // Increase height
                                              },
                                              paddingTop: "15px",
                                            }}
                                          />
                                        </div>
                                        <div className="col-auto">
                                          <input
                                            type="time"
                                            className="form-control"
                                            value={timeSlot.start || ""}
                                            style={{
                                              height: "30px",
                                              width: "100%",
                                            }}
                                            onChange={(e) =>
                                              handleTimeChange(
                                                shortDay,
                                                "start",
                                                e.target.value,
                                                index
                                              )
                                            }
                                          />
                                        </div>
                                        <div className="col-auto">
                                          <input
                                            type="time"
                                            className="form-control"
                                            value={timeSlot.end || ""}
                                            style={{ height: "30px" }}
                                            onChange={(e) =>
                                              handleTimeChange(
                                                shortDay,
                                                "end",
                                                e.target.value,
                                                index
                                              )
                                            }
                                          />
                                        </div>
                                        <div className="col-auto">
                                          <Checkbox
                                            checked={
                                              timeSlot.isShiftEnd || false
                                            }
                                            onChange={(e) => {
                                              const updatedTimes = {
                                                ...dayTimes,
                                              };
                                              updatedTimes[fullDay][
                                                index
                                              ].isShiftEnd = e.target.checked;
                                              setDayTimes(updatedTimes);
                                            }}
                                            sx={{
                                              color: orange[800],
                                              "&.Mui-checked": {
                                                color: orange[600],
                                              },
                                              "& .MuiSvgIcon-root": {
                                                // Add this style block to control size
                                                fontSize: 28, // Increase icon size (default is 24)
                                                width: "1.5em", // Increase width
                                                height: "1.5em", // Increase height
                                              },
                                            }}
                                          />
                                        </div>
                                        <div className="col-auto d-flex align-items-center">
                                          <button
                                            type="button" // Add this to prevent form submission
                                            onClick={(e) => {
                                              e.preventDefault();
                                              handleAddTimeSlot(fullDay, index);
                                            }}
                                            style={{
                                              color: "#FF8800",
                                              backgroundColor: "#eff0f4", // Gray background
                                              borderRadius: "50%", // Circle shape
                                              width: "28px",
                                              height: "28px",
                                              border: "none",
                                              display: "flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              marginRight: "5px",
                                            }}
                                          >
                                            +
                                          </button>
                                          {index > 0 && (
                                            <button
                                              type="button" // Add this to prevent form submission
                                              onClick={(e) => {
                                                e.preventDefault();
                                                handleRemoveTimeSlot(
                                                  fullDay,
                                                  index
                                                );
                                              }}
                                              style={{
                                                color: "red",
                                                backgroundColor: "#eff0f4", // Gray background
                                                borderRadius: "50%", // Circle shape
                                                width: "28px",
                                                height: "28px",
                                                border: "none",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginRight: "5px",
                                              }}
                                            >
                                              -
                                            </button>
                                          )}
                                          {/* Only show Copy button for Monday's first time slot */}
                                          {index === 0 &&
                                            fullDay === "Monday" && (
                                              <button
                                                type="button" // Add this to prevent form submission
                                                onClick={(e) =>
                                                  handleCopyToAll(fullDay, e)
                                                }
                                                style={{
                                                  backgroundColor:
                                                    "transparent",
                                                  border: "none",
                                                  color: "#FF8800",
                                                  padding: "4px 8px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  cursor: "pointer",
                                                  fontSize: "0.8rem",
                                                }}
                                                title="Copy time to all days"
                                              >
                                                <FaCopy
                                                  size={12}
                                                  style={{ marginRight: "4px" }}
                                                />
                                                Copy
                                              </button>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <div
                                  className="col-10"
                                  style={{ color: "grey", fontStyle: "italic" }}
                                >
                                  Rest Day
                                </div>
                              )}{" "}
                              {isSelected && !hasCheckedBox && (
                                <div className="col-12 text-danger text-sm mt-1">
                                  {/* Please select at least one checkbox (Shift Start or Shift End) */}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>

              <h5 className="font-semibold text-gray-700 bg-white mb-3">
                Breaks
              </h5>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                <span className="text-sm ms-2">
                  Schedule breaks by setting fixed times or durations here. If
                  left empty, members can clock into breaks freely.
                </span>

                <fieldset
                  disabled={!hasBreakMonitoring}
                 className={`form-group px-4 mt-3 ${!hasBreakMonitoring ? "feature-disabled" : ""}`}
                >
                  {breaks?.map((breakItem, index) => (
                    <div
                      key={index}
                      className="mb-4 border rounded p-3 position-relative"
                    >
                      <div className="row mb-3">
                        {/* Break Name and Duration */}
                        <div className="col-md-8 d-flex align-items-center gap-2">
                          <div className="col-6">
                            <label className="text-sm mb-1 text-gray-600">
                              Break Name
                            </label>
                            <input
                              type="text"
                              placeholder="Enter break name"
                              className="form-control"
                              value={breakItem.breakName}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "breakName",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div className="col-6">
                            <label className="text-sm mb-1 text-gray-600">
                              Duration
                            </label>
                            <div className="d-flex align-items-center gap-2">
                              <select
                                className="form-select"
                                value={breakItem.hours}
                                onChange={(e) =>
                                  handleDurationChange(
                                    index,
                                    "hours",
                                    e.target.value
                                  )
                                }
                                style={{ width: "65px", padding: "4px" }}
                              >
                                {[...Array(24)].map((_, i) => (
                                  <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                  </option>
                                ))}
                              </select>
                              <span>h</span>
                              <select
                                className="form-select"
                                value={breakItem.minutes}
                                onChange={(e) =>
                                  handleDurationChange(
                                    index,
                                    "minutes",
                                    e.target.value
                                  )
                                }
                                style={{ width: "65px", padding: "4px" }}
                              >
                                {[...Array(60)].map((_, i) => (
                                  <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                  </option>
                                ))}
                              </select>
                              <span>m</span>
                            </div>
                          </div>
                        </div>

                        {/* Paid/Unpaid Radio Buttons */}
                        <div className="col-md-3 d-flex align-items-end">
                          <div className="btn-group" role="group">
                            <button
                              type="button"
                              className={"btn px-4"}
                              onClick={() =>
                                handleInputChange(index, "paid", false)
                              }
                              style={{
                                backgroundColor: !breakItem.paid
                                  ? "#FF8800"
                                  : "white",
                                color: !breakItem.paid ? "white" : "#FF8800",
                                border: "1px solid #FF8800",
                                transition: "all 0.3s ease",
                                borderRadius: "4px 0 0 4px",
                              }}
                            >
                              Unpaid
                            </button>
                            <button
                              type="button"
                              className={"btn px-4"}
                              onClick={() =>
                                handleInputChange(index, "paid", true)
                              }
                              style={{
                                backgroundColor: breakItem.paid
                                  ? "#FF8800"
                                  : "white",
                                color: breakItem.paid ? "white" : "#FF8800",
                                border: "1px solid #FF8800",
                                transition: "all 0.3s ease",
                                borderRadius: "0 4px 4px 0",
                                borderLeft: "none",
                              }}
                            >
                              Paid
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Time Range Controls */}
                      <div className="row align-items-center">
                        <div className="col-12">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={breakItem.allowed_time_range?.enabled || false}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "enabled",
                                    e.target.checked
                                  )
                                }
                                sx={{
                                  color: orange[800],
                                  "&.Mui-checked": {
                                    color: orange[600],
                                  },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 24,
                                  },
                                }}
                              />
                            }
                            label="Allow break to be taken between"
                          />
                        </div>
                        {breakItem.allowed_time_range?.enabled && (
                          <div className="col-md-8 d-flex align-items-center gap-3 ms-4 mt-2">
                            <input
                              type="time"
                              className="form-control col-md-4"
                              value={breakItem.allowed_time_range?.start_time || ''}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "start_time",
                                  e.target.value
                                )
                              }
                            />
                            <span className="mx-2">to</span>
                            <input
                              type="time"
                              className="form-control col-md-4"
                              value={breakItem.allowed_time_range?.end_time || ''}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "end_time",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        )}
                      </div>

                      {/* Delete Break Button */}
                      <button
                        type="button"
                        className="btn btn-link text-danger position-absolute top-0 end-0 mt-2 me-2"
                        onClick={() => {
                          if (!hasBreakMonitoring) return;
                          handleRemoveBreak(index);
                        }}
                        disabled={!hasBreakMonitoring}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  ))}

                  {/* Add Break */}
                  <div
                    className="border mt-3 w-25 justify-content-center d-flex align-items-center rounded py-1 shadow-sm cursor-pointer"
                    onClick={(e) => {
                      if (!hasBreakMonitoring) return;
                      e.preventDefault();
                      handleAddBreak();
                    }}
                    style={{
                      pointerEvents: hasBreakMonitoring ? "auto" : "none",
                      opacity: hasBreakMonitoring ? 1 : 0.5,
                    }}
                  >
                    + Add a break
                  </div>
                </fieldset>
              </div>

              <h5 className="font-semibold text-gray-700 bg-white mb-3">
                Location
              </h5>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                <span className="mt-2 text-gray-1000 ">
                  {" "}
                  If gelocation Enforced,members assigned to this schedule will
                  only wil be allowed to track time withinthese locations .{" "}
                  {enableGeofencing && <span className="text-danger">*</span>}
                </span>
                {/* Other parts of the form remain the same */}
                <div className="col-sm-8 mb-4 bg-white">
                  <label className="mb-1">
                    Select Location
                    {enableGeofencing && (
                      <span className="text-danger ms-1">*</span>
                    )}
                  </label>
                  <Select
                    isMulti
                    options={locationOptions}
                    onChange={handleLocationChange}
                    value={locationOptions?.filter((option) =>
                      selectedLocationId?.includes(option.value)
                    )}
                    placeholder={
                      enableGeofencing
                        ? "Select Location (Required)"
                        : "Select Location (Optional)"
                    }
                    closeMenuOnSelect={false}
                    menuPlacement="top"
                  />
                  {enableGeofencing && selectedLocationId?.length === 0 && (
                    <small className="text-danger">
                      Location selection is required when geofencing is enabled
                    </small>
                  )}
                </div>
              </div>

              <h5 className="font-semibold text-gray-700 bg-white mb-3">
                Policies
              </h5>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2 ">
                <span className="font-semibold text-gray-700 bg-white my-2 d-block">
                  Overtime Rules
                </span>
                <span className="mt-2 text-gray-1000">
                  In combination with work hours, overtime will be shown
                  automatically in timesheets based on these rules.
                </span>

                <fieldset disabled={!hasOvertimeLogging} className={!hasOvertimeLogging ? "feature-disabled" : ""}
>
                  <div className="d-flex align-items-center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isOvertimeEnabled}
                          onChange={handleOvertimeSwitchChange}
                          sx={{
                            color: orange[800],
                            "&.Mui-checked": { color: orange[600] },
                            "& .MuiSvgIcon-root": {
                              fontSize: 28,
                              width: "1.5em",
                              height: "1.5em",
                            },
                            padding: "12px",
                          }}
                        />
                      }
                      label="Daily overtime"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.875rem",
                          marginLeft: "8px",
                        },
                      }}
                    />

                    {isOvertimeEnabled && (
                      <div
                        className="d-flex border rounded ms-auto"
                        style={{
                          width: "200px",
                          pointerEvents: hasOvertimeLogging ? "auto" : "none",
                          opacity: hasOvertimeLogging ? 1 : 0.5,
                        }}
                      >
                        {["Hours", "Time"].map((option) => (
                          <div
                            key={option}
                            className={`${
                              selectedOvertimeType === option
                                ? "bg-red-100 text-red-600 border border-red-400"
                                : "border-end"
                            } col text-center py-2 cursor-pointer`}
                            onClick={() =>
                              hasOvertimeLogging &&
                              handleOvertimeTypeClick(option)
                            }
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {isOvertimeEnabled && (
                    <div className="mb-3">
                      {selectedOvertimeType === "Time" ? (
                        <>
                          <div className="col-md-12 d-flex mb-2">
                            <label
                              className="text-sm col-md-5 mt-2"
                              htmlFor="overtimeStartBefore"
                            >
                              Overtime Starts Before
                            </label>
                            <input
                              type="time"
                              id="overtimeStartBefore"
                              className="form-control col-md-3"
                              value={overtimeStartBefore}
                              onChange={handleOvertimeStartBeforeChange}
                            />
                          </div>
                          <div className="col-md-12 d-flex">
                            <label
                              className="text-sm col-md-5 mt-2"
                              htmlFor="overtimeStartAfter"
                            >
                              Overtime Starts After
                            </label>
                            <input
                              type="time"
                              id="overtimeStartAfter"
                              className="form-control col-md-3"
                              value={overtimeStartAfter}
                              onChange={handleOvertimeStartAfterChange}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="col-md-12 d-flex">
                          <label
                            className="text-sm col-md-5 mt-2"
                            htmlFor="overtimeHours"
                          >
                            Overtime Hours
                          </label>
                          <input
                            type="time"
                            id="overtimeHours"
                            className="form-control col-md-3"
                            value={overtimeStartAfter}
                            onChange={handleOvertimeStartAfterChange}
                            placeholder="Enter hours"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <hr className="my-1" />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRestOvertimeEnabled}
                        onChange={(e) =>
                          setIsRestOvertimeEnabled(e.target.checked)
                        }
                        sx={{
                          color: orange[800],
                          "&.Mui-checked": {
                            color: orange[600],
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: 28,
                            width: "1.5em",
                            height: "1.5em",
                          },
                          padding: "12px",
                        }}
                      />
                    }
                    label="Rest day overtime"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.875rem",
                        marginLeft: "8px",
                      },
                    }}
                  />
                </fieldset>
              </div>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                <span className="font-semibold text-gray-700 bg-white my-2 d-block">
                  Location Tracking
                </span>
                <div className="row align-items-center">
                  <fieldset 
                  disabled={!hasLocationTracking} className={!hasLocationTracking ? "feature-disabled" : ""}
                  >
                    <div className="col-md-12">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enableLocationTracking}
                            onChange={(e) =>
                              setEnableLocationTracking(e.target.checked)
                            }
                            sx={{
                              color: orange[800],
                              "&.Mui-checked": {
                                color: orange[600],
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 28,
                                width: "1.5em",
                                height: "1.5em",
                              },
                              padding: "12px",
                            }}
                          />
                        }
                        label="Location Tracking Between CHECK-IN and CHECK-OUT"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                            marginLeft: "8px",
                          },
                        }}
                      />
                    </div>
                  </fieldset>
                </div>
              </div>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                <span className="font-semibold text-gray-700 bg-white my-2">
                  Geo Fencing
                </span>
                <div className="row align-items-center">
                  <fieldset disabled={!hasGeoFencing} className={!hasGeoFencing ? "feature-disabled" : ""}>
                    <div className="col-md-12">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={requireLocationForCheckInOut}
                            onChange={(e) =>
                              setRequireLocationForCheckInOut(e.target.checked)
                            }
                            sx={{
                              color: orange[800],
                              "&.Mui-checked": {
                                color: orange[600],
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 28,
                                width: "1.5em",
                                height: "1.5em",
                              },
                              padding: "12px",
                            }}
                          />
                        }
                        label="Require Location for CHECK-IN and CHECK-OUT"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                            marginLeft: "8px",
                          },
                        }}
                      />
                    </div>

                    <div className="col-md-12">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enableGeofencing}
                            onChange={(e) => {
                              setEnableGeofencing(e.target.checked);
                              if (
                                !e.target.checked &&
                                selectedLocationId?.length === 0
                              ) {
                                // No-op
                              }
                            }}
                            sx={{
                              color: orange[800],
                              "&.Mui-checked": {
                                color: orange[600],
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 28,
                                width: "1.5em",
                                height: "1.5em",
                              },
                              padding: "12px",
                            }}
                          />
                        }
                        label={
                          <span>
                            Enable geofencing
                            {enableGeofencing &&
                              selectedLocationId?.length === 0 && (
                                <span className="text-danger ms-2">
                                  (Location selection required)
                                </span>
                              )}
                          </span>
                        }
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                            marginLeft: "8px",
                          },
                        }}
                      />
                    </div>
                  </fieldset>
                </div>
              </div>


              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                <span className="font-semibold text-gray-700 bg-white my-2 d-block">
                  Automatic Clock Out
                </span>
                <div className="row align-items-center">
                  <fieldset disabled={!hasAutoCheckout} className={!hasAutoCheckout ? "feature-disabled" : ""}>
                    <div className="col-12">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allowAutoCheckOut}
                            onChange={(e) =>
                              setAllowAutoCheckOut(e.target.checked)
                            }
                            sx={{
                              color: orange[800],
                              "&.Mui-checked": {
                                color: orange[600],
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 28,
                                width: "1.5em",
                                height: "1.5em",
                              },
                              padding: "12px",
                            }}
                          />
                        }
                        label="Allow Automatic Check-Out"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                            marginLeft: "8px",
                          },
                        }}
                      />
                    </div>

                    {allowAutoCheckOut && (
                      <div className="col-12 mt-2" style={{ paddingLeft: "3rem" }}>
                        <div className="d-flex align-items-center gap-3" style={{ marginBottom: "10px" }}>
                          <div className="form-check mb-0">
                            <input
                              className="form-check-input artha-radio"
                              type="radio"
                              name="autoCheckOutOption"
                              id="radioAfterSchedule"
                              checked={autoCheckOutOption === "after_schedule"}
                              onChange={() => hasAutoCheckout && handleAutoCheckOutOptionChange("after_schedule")}
                              disabled={!hasAutoCheckout}
                            />
                            <label 
                              className="form-check-label text-sm" 
                              htmlFor="radioAfterSchedule"
                              style={{ cursor: hasAutoCheckout ? "pointer" : "not-allowed" }}
                            >
                              After work schedule ends
                            </label>
                          </div>

                          <div className="form-check mb-0">
                            <input
                              className="form-check-input artha-radio"
                              type="radio"
                              name="autoCheckOutOption"
                              id="radioSpecificTime"
                              checked={autoCheckOutOption === "specific_time"}
                              onChange={() => hasAutoCheckout && handleAutoCheckOutOptionChange("specific_time")}
                              disabled={!hasAutoCheckout}
                            />
                            <label 
                              className="form-check-label text-sm" 
                              htmlFor="radioSpecificTime"
                              style={{ cursor: hasAutoCheckout ? "pointer" : "not-allowed" }}
                            >
                              At a specific time
                            </label>
                          </div>

                          {autoCheckOutOption === "specific_time" && (
                            <input
                              type="time"
                              id="autoCheckOutAt"
                              className="form-control form-control-sm"
                              style={{ width: "130px" }}
                              value={autoCheckOutAt}
                              onChange={handleautoCheckOutAtChange}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    <div className="col-12 mt-3">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkInOutWithinPremises}
                            onChange={(e) =>
                              setCheckInOutWithinPremises(e.target.checked)
                            }
                            sx={{
                              color: orange[800],
                              "&.Mui-checked": {
                                color: orange[600],
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 28,
                                width: "1.5em",
                                height: "1.5em",
                              },
                              padding: "12px",
                            }}
                          />
                        }
                        label="Automatically check out the user when they exit the premises."
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                            marginLeft: "8px",
                          },
                        }}
                      />
                    </div>
                  </fieldset>
                </div>
              </div>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                <span className="font-semibold text-gray-700 bg-white my-2 d-block">
                  Application Usage
                </span>

                <fieldset 
                disabled={!hasAppUsageMonitoring} className={!hasAppUsageMonitoring ? "feature-disabled" : ""}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableDigitalWellbeing}
                        onChange={(e) =>
                          setEnableDigitalWellbeing(e.target.checked)
                        }
                        sx={{
                          color: orange[800],
                          "&.Mui-checked": {
                            color: orange[600],
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: 28,
                            width: "1.5em",
                            height: "1.5em",
                          },
                          padding: "12px",
                        }}
                      />
                    }
                    label="Enable Digital Wellbeing Between CHECK-IN and CHECK-OUT"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.875rem",
                        marginLeft: "8px",
                      },
                    }}
                  />
                </fieldset>
              </div>

              <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                <span className="font-semibold text-gray-700 bg-white my-2 d-block">
                Activity Snapshot Audit
                </span>

                <fieldset
                 disabled={!hasActivitySnapshotAuditing} className={!hasActivitySnapshotAuditing ? "feature-disabled" : ""}
                 >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={storeActivityImages}
                        onChange={(e) =>
                          setStoreActivityImages(e.target.checked)
                        }
                        sx={{
                          color: orange[800],
                          "&.Mui-checked": {
                            color: orange[600],
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: 28,
                            width: "1.5em",
                            height: "1.5em",
                          },
                          padding: "12px",
                        }}
                      />
                    }
                    label="Store the images captured during user activities like check-in, check-out etc."
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.875rem",
                        marginLeft: "8px",
                      },
                    }}
                  />
                </fieldset>
              </div>

              <div className="flex justify-end mt-4">
                <ArthaCancelButton
                  type="button"
                  onClick={handleCancelClick} // Add cancel button functionality
                >
                  Cancel
                </ArthaCancelButton>
                <ArthaContinueButton
                  type="submit"
                  disabled={!isFormValid() || isSuspended}
                  title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                  style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                >
                  Submit
                </ArthaContinueButton>
              </div>
            </form>
          ) : scheduleData ? (
            <div>
              <form
                className="card bg-white mx-auto p-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                // Apply scrollbar hiding styles to the form
              >
                <div className="col-12 col-md-12 d-flex">
                  <h5 className=" font-semibold text-gray-700 mb-3">
                    {isEditing ? "Edit Schedule" : "Schedule"}
                  </h5>
                </div>

                <div
                  style={{
                    pointerEvents: !isEditing ? "none" : "auto",
                    opacity: !isEditing ? 1 : 1,
                  }}
                >
                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-4 py-2 mt-2">
                    <div className="form-group row">
                      <label
                        htmlFor="scheduleName"
                        className="col-sm-3 text-sm"
                      >
                        Schedule Name :
                      </label>
                      <div className="col-sm-8">
                        <input
                          type="text"
                          id="scheduleName"
                          value={scheduleData.schedule_name}
                          placeholder="Enter Schedule Name"
                          onChange={(e) => setScheduleName(e.target.value)}
                          className="form-control text-sm"
                        />
                      </div>
                    </div>

                    {/* Work Arrangement */}
                    <div className="form-group row">
                      <label
                        htmlFor="workArrangement"
                        className="col-sm-3 col-form-label"
                      >
                        Work Arrangement:
                      </label>
                      <div className="col-sm-8">
                        <div className="d-flex border rounded">
                          {["Fixed", "Flexible", "Weekly"].map((option) => (
                            <div
                              key={option}
                              // className={optionStyle(option)}
                              className={`${
                                selectedWorkArrangement === option
                                  ? "bg-red-100 text-red-600 border border-red-400"
                                  : "border-end"
                              } col text-center py-2 cursor-pointer ${
                                option !== "Fixed" && option !== "Flexible"
                                  ? "disabled"
                                  : ""
                              }`}
                              onClick={() => handleWorkArrangementClick(option)}
                            >
                              {option}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Days of the Week */}
                    <div className="form-group row mt-3">
                      <label
                        htmlFor="daysOfWeek"
                        className="col-sm-3 col-form-label"
                      >
                        Days of the Week:
                      </label>
                      <div className="col-sm-8">
                        <div className="d-flex border rounded">
                          {[
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                            "Sun",
                          ].map((day) => (
                            <div
                              key={day}
                              className={dayStyle(day)}
                              onClick={() => handleDayClick(day)}
                            >
                              {/* {day === 'T2' ? 'Th' : day} */}
                              {day}
                            </div>
                          ))}
                        </div>
                        {selectedDays?.length === 0 && (
                          <span
                            className="text-sm fw-semibold"
                            style={{ color: "red" }}
                          >
                            Select at least one day of the week
                          </span>
                        )}
                      </div>
                    </div>

                    {selectedOption === "Flexible" ? (
                      <div className="mt-3" style={{ marginLeft: "20%" }}>
                        {Object.entries(shortToFullDay).map(
                          ([shortDay, fullDay]) => {
                            const isSelected = selectedDays.includes(shortDay);
                            return (
                              <div
                                key={shortDay}
                                className="form-group"
                                onMouseEnter={() => handleMouseEnter(fullDay)}
                                onMouseLeave={handleMouseLeave}
                              >
                                <div className="d-flex align-items-center mb-2">
                                  <div className="col-3 text-sm ">
                                    {fullDay}
                                  </div>
                                  {isSelected ? (
                                    <>
                                      <input
                                        type="time"
                                        className="form-control col-3 mx-2"
                                        value={dayTimes[fullDay]?.time || ""}
                                        onChange={(e) =>
                                          handleTimeChangeFlexible(
                                            fullDay,
                                            e.target.value
                                          )
                                        }
                                      />
                                      {hoveredDay === fullDay && (
                                        <button
                                          type="button" // Add this to prevent form submission
                                          onClick={(e) => {
                                            e.preventDefault();
                                            copyTimesFromFirst("Monday");
                                          }}
                                          style={{
                                            backgroundColor: "transparent",
                                            border: "none",
                                            color: "#FF8800",
                                            padding: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            cursor: "pointer",
                                          }}
                                          className="text-sm"
                                          title="Copy time to all days"
                                        >
                                          <FaCopy
                                            size={10}
                                            style={{ marginRight: "4px" }}
                                          />
                                          Copy
                                        </button>
                                      )}
                                    </>
                                  ) : (
                                    <div
                                      className="col-3"
                                      style={{
                                        color: "grey",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Rest Day
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    ) : (
                      <div
                        className=" mt-3 col-12 col-md-11 text-sm border rounded mt-2 py-2 "
                        style={{ marginLeft: "11%" }}
                      >
                        <div className="row pb-2 border-bottom">
                          <div className="col-2 ">Days</div>
                          <div className="col-10">
                            <div className="row ">
                              <div className="col-auto">Shift Start</div>
                              <div className="col-3">From Time</div>
                              <div className="col-2">To Time</div>
                              <div className="col-auto ms-3">Shift End</div>
                            </div>
                          </div>
                        </div>
                        {Object.entries(shortToFullDay).map(
                          ([shortDay, fullDay]) => {
                            const isSelected = selectedDays?.includes(shortDay);
                            const daySlots = dayTimes[fullDay] || [];
                            const hasCheckedBox = daySlots.some(
                              (slot) => slot.isShiftStart || slot.isShiftEnd
                            );

                            return (
                              <div
                                key={shortDay}
                                className="row align-items-center  py-2 border-bottom text-sm"
                              >
                                <div
                                  className="col-2 "
                                  style={{ fontSize: "13px" }}
                                >
                                  {fullDay}
                                </div>
                                <div className="col-10">
                                  {isSelected ? (
                                    <>
                                      {(
                                        dayTimes[fullDay] || [
                                          { start: "", end: "" },
                                        ]
                                      ).map((timeSlot, index, array) => (
                                        <div key={index}>
                                          <div className="row text-center align-items-center">
                                            <div className="col-auto">
                                              <Checkbox
                                                checked={
                                                  timeSlot.isShiftStart || false
                                                }
                                                onChange={(e) => {
                                                  const updatedTimes = {
                                                    ...dayTimes,
                                                  };
                                                  updatedTimes[fullDay][
                                                    index
                                                  ].isShiftStart =
                                                    e.target.checked;
                                                  setDayTimes(updatedTimes);
                                                }}
                                                sx={{
                                                  color: orange[800],
                                                  "&.Mui-checked": {
                                                    color: orange[600],
                                                  },
                                                  "& .MuiSvgIcon-root": {
                                                    // Add this style block to control size
                                                    fontSize: 28, // Increase icon size (default is 24)
                                                    width: "1.5em", // Increase width
                                                    height: "1.5em", // Increase height
                                                  },
                                                  paddingTop: "15px",
                                                }}
                                              />
                                            </div>
                                            <div className="col-auto">
                                              <input
                                                type="time"
                                                className="form-control"
                                                value={timeSlot.start || ""}
                                                style={{
                                                  height: "30px",
                                                  width: "100%",
                                                }}
                                                onChange={(e) =>
                                                  handleTimeChange(
                                                    shortDay,
                                                    "start",
                                                    e.target.value,
                                                    index
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="col-auto">
                                              <input
                                                type="time"
                                                className="form-control"
                                                value={timeSlot.end || ""}
                                                style={{ height: "30px" }}
                                                onChange={(e) =>
                                                  handleTimeChange(
                                                    shortDay,
                                                    "end",
                                                    e.target.value,
                                                    index
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="col-auto">
                                              <Checkbox
                                                checked={
                                                  timeSlot.isShiftEnd || false
                                                }
                                                onChange={(e) => {
                                                  const updatedTimes = {
                                                    ...dayTimes,
                                                  };
                                                  updatedTimes[fullDay][
                                                    index
                                                  ].isShiftEnd =
                                                    e.target.checked;
                                                  setDayTimes(updatedTimes);
                                                }}
                                                sx={{
                                                  color: orange[800],
                                                  "&.Mui-checked": {
                                                    color: orange[600],
                                                  },
                                                  "& .MuiSvgIcon-root": {
                                                    // Add this style block to control size
                                                    fontSize: 28, // Increase icon size (default is 24)
                                                    width: "1.5em", // Increase width
                                                    height: "1.5em", // Increase height
                                                  },
                                                  paddingTop: "15px",
                                                }}
                                              />
                                            </div>
                                            <div className="col-auto d-flex align-items-center">
                                              <button
                                                type="button" // Add this to prevent form submission
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleAddTimeSlot(
                                                    fullDay,
                                                    index
                                                  );
                                                }}
                                                style={{
                                                  color: "#FF8800",
                                                  backgroundColor: "#eff0f4", // Gray background
                                                  borderRadius: "50%", // Circle shape
                                                  width: "28px",
                                                  height: "28px",
                                                  border: "none",
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  alignItems: "center",
                                                  marginRight: "5px",
                                                }}
                                              >
                                                +
                                              </button>
                                              {index > 0 && (
                                                <button
                                                  type="button" // Add this to prevent form submission
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    handleRemoveTimeSlot(
                                                      fullDay,
                                                      index
                                                    );
                                                  }}
                                                  style={{
                                                    color: "red",
                                                    backgroundColor: "#eff0f4", // Gray background
                                                    borderRadius: "50%", // Circle shape
                                                    width: "28px",
                                                    height: "28px",
                                                    border: "none",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    marginRight: "5px",
                                                  }}
                                                >
                                                  -
                                                </button>
                                              )}
                                              {/* Only show Copy button for Monday's first time slot */}
                                              {index === 0 &&
                                                fullDay === "Monday" && (
                                                  <button
                                                    type="button" // Add this to prevent form submission
                                                    onClick={(e) =>
                                                      handleCopyToAll(
                                                        fullDay,
                                                        e
                                                      )
                                                    }
                                                    style={{
                                                      backgroundColor:
                                                        "transparent",
                                                      border: "none",
                                                      color: "#FF8800",
                                                      padding: "4px 8px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      cursor: "pointer",
                                                      fontSize: "0.8rem",
                                                    }}
                                                    title="Copy time to all days"
                                                  >
                                                    <FaCopy
                                                      size={12}
                                                      style={{
                                                        marginRight: "4px",
                                                      }}
                                                    />
                                                    Copy
                                                  </button>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  ) : (
                                    <div
                                      className="col-10"
                                      style={{
                                        color: "grey",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Rest Day
                                    </div>
                                  )}{" "}
                                  {isSelected && !hasCheckedBox && (
                                    <div className="col-12 text-danger text-sm mt-1">
                                      {/* Please select at least one checkbox (Shift Start or Shift End) */}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>

                  <h5 className="font-semibold text-gray-700 bg-white mb-2">
                    Breaks
                  </h5>
                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                    <span className="text-sm ms-2 ">
                      Schedule breaks by setting fixed times or durations here.
                      If left empty, members can clock into breaks freely.
                    </span>

                    <div className="form-group px-4 mt-3">
                      {breaks?.map((breakItem, index) => (
                        <div key={index} className="mb-4 border rounded p-3">
                          <div className="row mb-3">
                            {/* Break Name and Duration */}
                            <div className="col-md-8 d-flex align-items-center gap-3">
                              <div className="col-6">
                                <label className="text-sm mb-1 text-gray-600">
                                  Break Name
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter break name"
                                  className="form-control"
                                  value={breakItem.breakName}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "breakName",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="col-6">
                                <label className="text-sm mb-1 text-gray-600">
                                  Duration
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  <select
                                    className="form-select"
                                    value={breakItem.hours}
                                    onChange={(e) =>
                                      handleDurationChange(
                                        index,
                                        "hours",
                                        e.target.value
                                      )
                                    }
                                    style={{ width: "65px", padding: "4px" }}
                                  >
                                    {[...Array(24)].map((_, i) => (
                                      <option key={i} value={i}>
                                        {i.toString().padStart(2, "0")}
                                      </option>
                                    ))}
                                  </select>
                                  <span>h</span>
                                  <select
                                    className="form-select"
                                    value={breakItem.minutes}
                                    onChange={(e) =>
                                      handleDurationChange(
                                        index,
                                        "minutes",
                                        e.target.value
                                      )
                                    }
                                    style={{ width: "65px", padding: "4px" }}
                                  >
                                    {[...Array(60)].map((_, i) => (
                                      <option key={i} value={i}>
                                        {i.toString().padStart(2, "0")}
                                      </option>
                                    ))}
                                  </select>
                                  <span>m</span>
                                </div>
                              </div>
                            </div>

                            {/* Paid/Unpaid Radio Buttons */}
                            <div className="col-md-4 d-flex align-items-end">
                              <div className="btn-group" role="group">
                                <button
                                  type="button"
                                  className={`btn px-4`}
                                  onClick={() =>
                                    handleInputChange(index, "paid", false)
                                  }
                                  style={{
                                    backgroundColor: !breakItem.paid
                                      ? "#FF8800"
                                      : "white",
                                    color: !breakItem.paid
                                      ? "white"
                                      : "#FF8800",
                                    border: "1px solid #FF8800",
                                    transition: "all 0.3s ease",
                                    borderRadius: breakItem.paid
                                      ? "4px 0 0 4px"
                                      : "4px 0 0 4px",
                                  }}
                                >
                                  Unpaid
                                </button>
                                <button
                                  type="button"
                                  className={`btn px-4`}
                                  onClick={() =>
                                    handleInputChange(index, "paid", true)
                                  }
                                  style={{
                                    backgroundColor: breakItem.paid
                                      ? "#FF8800"
                                      : "white",
                                    color: breakItem.paid ? "white" : "#FF8800",
                                    border: "1px solid #FF8800",
                                    transition: "all 0.3s ease",
                                    borderRadius: "0 4px 4px 0",
                                    borderLeft: "none",
                                  }}
                                >
                                  Paid
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Time Range Controls */}
                          <div className="row align-items-center">
                            <div className="col-12">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      breakItem.allowed_time_range?.enabled || false
                                    }
                                    onChange={(e) =>
                                      handleInputChange(
                                        index,
                                        "enabled",
                                        e.target.checked
                                      )
                                    }
                                    sx={{
                                      color: orange[800],
                                      "&.Mui-checked": {
                                        color: orange[600],
                                      },
                                      "& .MuiSvgIcon-root": {
                                        fontSize: 24,
                                      },
                                    }}
                                  />
                                }
                                label="Allow break to be taken between"
                              />
                            </div>
                            {breakItem.allowed_time_range?.enabled && (
                              <div className="col-md-8 d-flex align-items-center gap-3 ms-4 mt-2">
                                <input
                                  type="time"
                                  className="form-control col-md-4"
                                  value={
                                    breakItem.allowed_time_range?.start_time || ''
                                  }
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "start_time",
                                      e.target.value
                                    )
                                  }
                                />
                                <span className="mx-2">to</span>
                                <input
                                  type="time"
                                  className="form-control col-md-4"
                                  value={breakItem.allowed_time_range?.end_time || ''}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "end_time",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            )}
                          </div>

                          {/* Delete Break Button */}
                          <button
                            type="button" // Add this to prevent form submission
                            className="btn btn-link text-danger position-absolute top-0 end-0 mt-2 me-2"
                            onClick={() => handleRemoveBreak(index)}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      ))}

                      <div
                        className="border mt-3 w-25 justify-content-center d-flex align-items-center rounded py-1 shadow-sm cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault(); // Prevent form submission
                          handleAddBreak();
                        }}
                      >
                        + Add a break
                      </div>
                    </div>
                  </div>

                  <h5 className=" font-semibold text-gray-700 bg-white  mb-2">
                    Location
                  </h5>
                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                    <span className="mt-2 text-gray-1000 ">
                      {" "}
                      If gelocation Enforced,members assigned to this schedule
                      will only wil be allowed to track time withinthese
                      locations .{" "}
                      {enableGeofencing && (
                        <span className="text-danger">*</span>
                      )}
                    </span>
                    {/* Other parts of the form remain the same */}
                    <div className="col-sm-8 mb-4 bg-white">
                      <label className="mb-1">
                        Select Location
                        {enableGeofencing && (
                          <span className="text-danger ms-1">*</span>
                        )}
                      </label>
                      <Select
                        isMulti
                        options={locationOptions}
                        onChange={handleLocationChange}
                        value={locationOptions?.filter((option) =>
                          selectedLocationId?.includes(option.value)
                        )}
                        placeholder={
                          enableGeofencing
                            ? "Select Location (Required)"
                            : "Select Location (Optional)"
                        }
                        closeMenuOnSelect={false}
                        menuPlacement="top"
                      />
                      {enableGeofencing && selectedLocationId?.length === 0 && (
                        <small className="text-danger">
                          Location selection is required when geofencing is
                          enabled
                        </small>
                      )}
                    </div>
                  </div>

                  <h5 className="font-semibold text-gray-700 bg-white  mb-2">
                    Policies
                  </h5>

                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                    <span className=" font-semibold text-gray-700 bg-white my-2 d-block">
                      Overtime Rules
                    </span>
                    <span className="mt-2 text-gray-1000 ">
                      {" "}
                      In combination with work hours, overtime will be shown
                      automatically in timesheets based on these rules.{" "}
                    </span>

                    <div className="d-flex align-items-center mb-3">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isOvertimeEnabled}
                            onChange={handleOvertimeSwitchChange}
                            sx={{
                              color: orange[800],
                              "&.Mui-checked": {
                                color: orange[600],
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 28,
                                width: "1.5em",
                                height: "1.5em",
                              },
                              padding: "12px",
                            }}
                          />
                        }
                        label="Daily overtime"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.875rem",
                            marginLeft: "8px",
                          },
                        }}
                      />

                      {isOvertimeEnabled && (
                        <div
                          className="d-flex border rounded ms-auto"
                          style={{ width: "200px" }}
                        >
                          {["Hours", "Time"].map((option) => (
                            <div
                              key={option}
                              className={`${
                                selectedOvertimeType === option
                                  ? "bg-red-100 text-red-600 border border-red-400"
                                  : "border-end"
                              } col text-center py-2 cursor-pointer`}
                              onClick={() => handleOvertimeTypeClick(option)}
                            >
                              {option}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {isOvertimeEnabled && (
                      <div className="mb-3">
                        {selectedOvertimeType === "Time" ? (
                          <>
                            <div className="col-md-12 d-flex mb-2">
                              <label
                                className="text-sm col-md-5 mt-2"
                                htmlFor="overtimeStartBefore"
                              >
                                Overtime Starts Before
                              </label>
                              <input
                                type="time"
                                id="overtimeStartBefore"
                                className="form-control col-md-3"
                                value={overtimeStartBefore}
                                onChange={handleOvertimeStartBeforeChange}
                              />
                            </div>
                            <div className="col-md-12 d-flex">
                              <label
                                className="text-sm col-md-5 mt-2"
                                htmlFor="overtimeStartAfter"
                              >
                                Overtime Starts After
                              </label>
                              <input
                                type="time"
                                id="overtimeStartAfter"
                                className="form-control col-md-3"
                                value={overtimeStartAfter}
                                onChange={handleOvertimeStartAfterChange}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="col-md-12 d-flex">
                            <label
                              className="text-sm col-md-5 mt-2"
                              htmlFor="overtimeHours"
                            >
                              Overtime Hours
                            </label>
                            <input
                              type="time"
                              id="overtimeHours"
                              className="form-control col-md-3"
                              value={overtimeStartAfter}
                              onChange={handleOvertimeStartAfterChange}
                              placeholder="Enter hours"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <hr className="my-1" />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isRestOvertimeEnabled}
                          onChange={(e) =>
                            setIsRestOvertimeEnabled(e.target.checked)
                          }
                          sx={{
                            color: orange[800],
                            "&.Mui-checked": {
                              color: orange[600],
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: 28,
                              width: "1.5em",
                              height: "1.5em",
                            },
                            padding: "12px",
                          }}
                        />
                      }
                      label="Rest day overtime"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.875rem",
                          marginLeft: "8px",
                        },
                      }}
                    />
                  </div>

                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                    <span className=" font-semibold text-gray-700 bg-white my-2 d-block">
                      Location
                    </span>
                    <div className="row align-items-center">
                      <div className="col-md-12">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={enableLocationTracking}
                              onChange={(e) =>
                                setEnableLocationTracking(e.target.checked)
                              }
                              sx={{
                                color: orange[800],
                                "&.Mui-checked": {
                                  color: orange[600],
                                },
                                "& .MuiSvgIcon-root": {
                                  // Add this style block to control size
                                  fontSize: 28, // Increase icon size (default is 24)
                                  width: "1.5em", // Increase width
                                  height: "1.5em", // Increase height
                                },
                                padding: "12px", // Increase padding around the checkbox
                              }}
                            />
                          }
                          label="Enable Location Tracking Between CHECK-IN and CHECK-OUT"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.875rem", // Adjust label size if needed
                              marginLeft: "8px", // Add some spacing between checkbox and label
                            },
                          }}
                        />
                      </div>
                      <div className="col-md-12">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={requireLocationForCheckInOut}
                              onChange={(e) =>
                                setRequireLocationForCheckInOut(
                                  e.target.checked
                                )
                              }
                              sx={{
                                color: orange[800],
                                "&.Mui-checked": {
                                  color: orange[600],
                                },
                                "& .MuiSvgIcon-root": {
                                  // Add this style block to control size
                                  fontSize: 28, // Increase icon size (default is 24)
                                  width: "1.5em", // Increase width
                                  height: "1.5em", // Increase height
                                },
                                padding: "12px", // Increase padding around the checkbox
                              }}
                            />
                          }
                          label="Require Location for CHECK-IN and CHECK-OUT"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.875rem", // Adjust label size if needed
                              marginLeft: "8px", // Add some spacing between checkbox and label
                            },
                          }}
                        />
                      </div>
                      <div className="col-md-12">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={enableGeofencing}
                              onChange={(e) => {
                                setEnableGeofencing(e.target.checked);
                                // If turning off geofencing and no locations selected, don't show validation errors
                                if (
                                  !e.target.checked &&
                                  selectedLocationId?.length === 0
                                ) {
                                  // We don't need to clear locations if none were selected
                                }
                              }}
                              sx={{
                                color: orange[800],
                                "&.Mui-checked": {
                                  color: orange[600],
                                },
                                "& .MuiSvgIcon-root": {
                                  fontSize: 28,
                                  width: "1.5em",
                                  height: "1.5em",
                                },
                                padding: "12px",
                              }}
                            />
                          }
                          label={
                            <span>
                              Enable geofencing
                              {enableGeofencing &&
                                selectedLocationId?.length === 0 && (
                                  <span className="text-danger ms-2">
                                    (Location selection required)
                                  </span>
                                )}
                            </span>
                          }
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.875rem",
                              marginLeft: "8px",
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                    <span className=" font-semibold text-gray-700 bg-white my-2 d-block">
                      Automatic Clock Out
                    </span>
                    <div className="row align-items-center">
                      <div className="col-12">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allowAutoCheckOut}
                              onChange={(e) =>
                                setAllowAutoCheckOut(e.target.checked)
                              }
                              sx={{
                                color: orange[800],
                                "&.Mui-checked": {
                                  color: orange[600],
                                },
                                "& .MuiSvgIcon-root": {
                                  // Add this style block to control size
                                  fontSize: 28, // Increase icon size (default is 24)
                                  width: "1.5em", // Increase width
                                  height: "1.5em", // Increase height
                                },
                                padding: "12px", // Increase padding around the checkbox
                              }}
                            />
                          }
                          label="Allow Automatic Check-Out"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.875rem", // Adjust label size if needed
                              marginLeft: "8px", // Add some spacing between checkbox and label
                            },
                          }}
                        />
                      </div>

                      {allowAutoCheckOut && (
                        <div className="col-12 mt-2" style={{ paddingLeft: "3rem" }}>
                          <div className="d-flex align-items-center gap-3" style={{ marginBottom: "10px" }}>
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input artha-radio"
                                type="radio"
                                name="autoCheckOutOption"
                                id="radioAfterSchedule2"
                                checked={autoCheckOutOption === "after_schedule"}
                                onChange={() => handleAutoCheckOutOptionChange("after_schedule")}
                              />
                              <label 
                                className="form-check-label text-sm" 
                                htmlFor="radioAfterSchedule2"
                                style={{ cursor: "pointer" }}
                              >
                                After work schedule ends
                              </label>
                            </div>

                            <div className="form-check mb-0">
                              <input
                                className="form-check-input artha-radio"
                                type="radio"
                                name="autoCheckOutOption"
                                id="radioSpecificTime2"
                                checked={autoCheckOutOption === "specific_time"}
                                onChange={() => handleAutoCheckOutOptionChange("specific_time")}
                              />
                              <label 
                                className="form-check-label text-sm" 
                                htmlFor="radioSpecificTime2"
                                style={{ cursor: "pointer" }}
                              >
                                At a specific time
                              </label>
                            </div>

                            {autoCheckOutOption === "specific_time" && (
                              <input
                                type="time"
                                id="autoCheckOutAt"
                                className="form-control form-control-sm"
                                style={{ width: "130px" }}
                                value={autoCheckOutAt}
                                onChange={handleautoCheckOutAtChange}
                              />
                            )}
                          </div>
                        </div>
                      )}

                      <div className="col-12 mt-3">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checkInOutWithinPremises}
                              onChange={(e) =>
                                setCheckInOutWithinPremises(e.target.checked)
                              }
                              sx={{
                                color: orange[800],
                                "&.Mui-checked": {
                                  color: orange[600],
                                },
                                "& .MuiSvgIcon-root": {
                                  fontSize: 28,
                                  width: "1.5em",
                                  height: "1.5em",
                                },
                                padding: "12px",
                              }}
                            />
                          }
                          label="Automatically check out the user when they exit the premises."
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.875rem",
                              marginLeft: "8px",
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                    <span className=" font-semibold text-gray-700 bg-white my-2 d-block">
                      Application Usage
                    </span>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enableDigitalWellbeing}
                          onChange={(e) =>
                            setEnableDigitalWellbeing(e.target.checked)
                          }
                          sx={{
                            color: orange[800],
                            "&.Mui-checked": {
                              color: orange[600],
                            },
                            "& .MuiSvgIcon-root": {
                              // Add this style block to control size
                              fontSize: 28, // Increase icon size (default is 24)
                              width: "1.5em", // Increase width
                              height: "1.5em", // Increase height
                            },
                            padding: "12px", // Increase padding around the checkbox
                          }}
                        />
                      }
                      label="Enable Digital Welbeing Between CHECK-IN and CHECK-OUT"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.875rem", // Adjust label size if needed
                          // Add some spacing between checkbox and label
                        },
                      }}
                    />
                  </div>

                  <div className="col-sm-10 col-md-12 mb-3 border rounded border-2 px-3 py-2">
                    <span className=" font-semibold text-gray-700 bg-white my-2 d-block">
                     Activity Snapshot Audit
                    </span>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enableStoreActivityImages}
                          onChange={(e) =>
                            setEnableStoreActivityImages(e.target.checked)
                          }
                          sx={{
                            color: orange[800],
                            "&.Mui-checked": {
                              color: orange[600],
                            },
                            "& .MuiSvgIcon-root": {
                              // Add this style block to control size
                              fontSize: 28, // Increase icon size (default is 24)
                              width: "1.5em", // Increase width
                              height: "1.5em", // Increase height
                            },
                            padding: "12px", // Increase padding around the checkbox
                          }}
                        />
                      }
                      label="Store the images captured during user activities like check-in, check-out etc."
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.875rem", // Adjust label size if needed
                          // Add some spacing between checkbox and label
                        },
                      }}
                    />
                  </div>
                </div>

                <div
                  className="flex justify-end mt-4"
                  style={{
                    pointerEvents: !isEditing ? "none" : "auto",
                    display: isEditing ? "flex" : "none",
                  }}
                >
                  <ArthaCancelButton
                    type="button"
                    onClick={handleCancelClick} // Add cancel button functionality
                  >
                    Cancel
                  </ArthaCancelButton>
                  <ArthaContinueButton
                    disabled={!isFormValid() || isSuspended}
                    onClick={isSuspended ? undefined : handleUpdate}
                    title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                    style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                  >
                    Save
                  </ArthaContinueButton>
                </div>
              </form>
            </div>
          ) : (
            <div>Please select a schedule to view the details</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddWorkSchedule;
